import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  BusinessSettings,
  createDefaultBusinessSettings,
  deleteBusinessSetting,
  getBusinessSettings,
  updateDefaultBusinessSettings,
  upsertBusinessSettings,
} from '~/services/BusinessTraveller/BusinessTravellerService';

// `businessId` is undefined for the global defaults scope and a UUID for a specific business.
export const businessSettingsQueryKeys = {
  all: ['business-settings'] as const,
  details: () => [...businessSettingsQueryKeys.all, 'detail'] as const,
  detail: (businessId: string | undefined) =>
    [...businessSettingsQueryKeys.details(), businessId ?? 'default'] as const,
};

export function useGetBusinessSettings(businessId?: string) {
  return useQuery({
    queryKey: businessSettingsQueryKeys.detail(businessId),
    queryFn: () => getBusinessSettings(businessId),
    // Empty string means the caller's businessId isn't ready yet (e.g. route still resolving).
    // Undefined is a valid scope (global defaults) and should run.
    enabled: businessId !== '',
  });
}

export function useUpsertBusinessSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (payload: { businessId: string; settings: BusinessSettings }) => upsertBusinessSettings(payload),
    onSuccess: (_data, variables) => {
      // Mark the cached settings stale so any mounted useGetBusinessSettings refetches and reflects the change.
      queryClient.invalidateQueries({ queryKey: businessSettingsQueryKeys.detail(variables.businessId) });
      enqueueSnackbar('Settings saved', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error saving settings: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}

export function useCreateDefaultBusinessSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (settings: BusinessSettings) => createDefaultBusinessSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessSettingsQueryKeys.detail(undefined) });
      enqueueSnackbar('Default settings created', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating default settings: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}

export function useUpdateDefaultBusinessSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (payload: { id: string; settings: BusinessSettings }) =>
      updateDefaultBusinessSettings(payload.id, payload.settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessSettingsQueryKeys.detail(undefined) });
      enqueueSnackbar('Default settings saved', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error saving default settings: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}

export function useDeleteBusinessSetting(businessId?: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => deleteBusinessSetting(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessSettingsQueryKeys.detail(businessId) });
      enqueueSnackbar('Setting deleted', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error deleting setting: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
